// v2 arctic hare — a hare sitting bolt upright on its haunches the way arctic
// hares scan the tundra: big rounded haunches with long snowshoe hind feet
// planted forward, slim front legs propped straight, a plump chest-up body, a
// round head with the short black-tipped ears (the arctic field mark), a
// grey-pink nose and a cotton tail. Two coats: winter white and the blue-grey
// summer moult. Ported 1:1 from age-of-mcp's v2/arctic-hare.ts, using the
// library's shared `wetland` (eyes) helper.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const COATS = [
  { body: "#f4f6f8", chest: "#ffffff", head: "#fbfcfd", haunch: "#eceff2",
    foot: "#e8ebee", ear: "#f0f2f4" },
  { body: "#9aa0a8", chest: "#c2c6cc", head: "#a8aeb6", haunch: "#8a9098",
    foot: "#b2b8be", ear: "#9aa0a8" },
];

function hareParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = [
    // big haunches with long snowshoe hind feet planted forward
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.haunch,
      position: [-0.022, 0.028, 0.016], scale: [1.1, 1.15, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.haunch,
      position: [-0.022, 0.028, -0.016], scale: [1.1, 1.15, 0.85] },
    { geometry: new THREE.BoxGeometry(0.034, 0.009, 0.013), color: c.foot,
      position: [0.005, 0.0045, 0.02] },
    { geometry: new THREE.BoxGeometry(0.034, 0.009, 0.013), color: c.foot,
      position: [0.005, 0.0045, -0.02] },
    // slim front legs propped straight under the chest
    { geometry: new THREE.CylinderGeometry(0.0045, 0.004, 0.032, 4), color: c.body,
      position: [0.022, 0.016, 0.009] },
    { geometry: new THREE.CylinderGeometry(0.0045, 0.004, 0.032, 4), color: c.body,
      position: [0.022, 0.016, -0.009] },
    // upright body, chest high
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: c.body,
      position: [0, 0.042, 0], scale: [1.15, 1.25, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.chest,
      position: [0.018, 0.038, 0], scale: [0.9, 1.1, 0.9] },
    // round head with muzzle and grey-pink nose
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.head,
      position: [0.03, 0.082, 0], scale: [1.05, 1.0, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.01, 0), color: c.chest,
      position: [0.044, 0.076, 0], scale: [0.9, 0.8, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.0028, 0), color: "#c89098",
      position: [0.052, 0.078, 0] },
    ...wetland.eyes(0.038, 0.088, 0.0125, 0.0032, "#2a2622"),
    // cotton tail
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.chest,
      position: [-0.044, 0.04, 0] },
  ];
  // short ears, each capped with the black arctic tip
  for (const s of [1, -1]) {
    parts.push(
      { geometry: new THREE.BoxGeometry(0.007, 0.032, 0.005), color: c.ear,
        position: [0.022, 0.112, s * 0.0095], rotation: [s * 0.12, 0, 0.18] },
      { geometry: new THREE.BoxGeometry(0.0074, 0.008, 0.0054), color: "#22221f",
        position: [0.019, 0.1275, s * 0.0115], rotation: [s * 0.12, 0, 0.18] },
    );
  }
  return parts;
}

export const arcticHareV2 = defineModel({
  id: "arctic-hare-v2",
  batchId: "fable",
  name: "Arctic hare (v2)",
  tags: ["creature","arctic","hare","low-poly","age-of-mcp","fable"],
  summary: "Low-poly arctic hare.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.07,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => hareParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => hareParts(1) },
  ],
  animations: [],
});
